import { useCallback, useEffect, useState } from "react";
import type { ReviewQueueItem, UserApplication, MediaAsset } from "../domain/types";
import { reviewQueueStore, type ReviewFilter } from "../review/queueStore";
import { fetchModerationQueue, moderateUser, mediaAbsoluteUrl } from "../api/backend";
import { apiJson } from "../api/client";

export type ServerQueueItem = {
  userId: string;
  handle: string;
  firstName: string;
  age: number;
  city: string;
  gender: string;
  profession: string;
  aboutYou: string;
  lookingFor: string;
  hobbies: string;
  media: Array<{
    id: string;
    kind: string;
    mimeType: string;
    sizeBytes: number;
    viewPath: string;
  }>;
};

function serverItemToApp(item: ServerQueueItem): UserApplication {
  const now = new Date().toISOString();
  const toAsset = (m: ServerQueueItem["media"][0]): MediaAsset => ({
    id: m.id,
    kind: m.kind as MediaAsset["kind"],
    fileName: m.kind,
    mimeType: m.mimeType,
    sizeBytes: m.sizeBytes,
    objectUrl: "",
    storageKey: null,
    publicUrl: mediaAbsoluteUrl(m.viewPath),
    uploadStatus: "uploaded",
    reviewStatus: "pending_review",
    createdAt: now,
    reviewNote: null,
  });

  const idMedia = item.media.find((m) => m.kind === "government_id");
  const video = item.media.find((m) => m.kind === "confirmation_video");
  const photos = item.media.filter((m) => m.kind === "profile_photo").map(toAsset);

  return {
    id: item.userId,
    userId: item.userId,
    status: "pending_review",
    createdAt: now,
    updatedAt: now,
    isChristian: true,
    rules: {
      respectful: true,
      noSexualContent: true,
      noDominance: true,
      acceptedAt: now,
    },
    intent: null,
    firstName: item.firstName,
    age: String(item.age || ""),
    city: item.city,
    gender: item.gender === "female" ? "female" : "male",
    seeking: item.gender === "female" ? "male" : "female",
    email: item.handle,
    passwordSet: true,
    profession: item.profession,
    aboutYou: item.aboutYou,
    lookingFor: item.lookingFor,
    hobbies: item.hobbies,
    whyChristian: "",
    loveReady: true,
    reading: null,
    scales: { loyalty: null, honesty: null, passion: null, commitment: null },
    valueAnswers: { loyalty: null, honesty: null, passion: null, commitment: null },
    verification: {
      id: `ver_${item.userId}`,
      userId: item.userId,
      status: "pending_review",
      governmentId: idMedia ? toAsset(idMedia) : null,
      confirmationVideo: video ? toAsset(video) : null,
      profilePhotos: photos,
      submittedAt: now,
      reviewedAt: null,
      reviewerId: null,
      reviewNote: null,
    },
  };
}

export function useReviewQueue(filter: ReviewFilter) {
  const [items, setItems] = useState<ReviewQueueItem[]>([]);
  const [serverApps, setServerApps] = useState<Map<string, UserApplication>>(new Map());
  const [tick, setTick] = useState(0);
  const [source, setSource] = useState<"local" | "server">("local");

  const refresh = useCallback(async () => {
    // Prefer live moderation queue whenever this session is a moderator.
    // Do not require authMode === "server" — that left identity media on the
    // local queue (no R2 tickets) and stuck MediaPreview on “Resolving…”.
    const me = await apiJson<{ user?: { isModerator?: boolean } }>("/api/me", "GET");
    if (me.ok && me.data.user?.isModerator) {
      const queue = (await fetchModerationQueue()) as ServerQueueItem[];
      const map = new Map<string, UserApplication>();
      const list: ReviewQueueItem[] = [];
      for (const item of queue) {
        const app = serverItemToApp(item);
        map.set(app.id, app);
        list.push({
          applicationId: app.id,
          userId: app.userId,
          email: item.handle,
          firstName: app.firstName,
          submittedAt: app.verification.submittedAt || app.updatedAt,
          status: "pending_review",
          mediaSummary: {
            hasId: Boolean(app.verification.governmentId),
            hasVideo: Boolean(app.verification.confirmationVideo),
            photoCount: app.verification.profilePhotos.length,
          },
          mediaIds: item.media.map((m) => m.id),
        });
      }
      setServerApps(map);
      setSource("server");
      if (filter === "pending_review" || filter === "all") {
        setItems(list);
      } else {
        setItems([]);
      }
      setTick((t) => t + 1);
      return;
    }
    setSource("local");
    setItems(reviewQueueStore.listByStatus(filter));
    setTick((t) => t + 1);
  }, [filter]);

  useEffect(() => {
    void refresh();
    return reviewQueueStore.subscribe(() => {
      void refresh();
    });
  }, [refresh]);

  return { items, refresh, tick, source, serverApps };
}

export function useReviewApplication(
  applicationId: string | null,
  serverApps?: Map<string, UserApplication>
) {
  const [app, setApp] = useState<UserApplication | null>(null);

  const refresh = useCallback(() => {
    if (!applicationId) {
      setApp(null);
      return;
    }
    if (serverApps?.has(applicationId)) {
      setApp(serverApps.get(applicationId) || null);
      return;
    }
    setApp(reviewQueueStore.getApplication(applicationId));
  }, [applicationId, serverApps]);

  useEffect(() => {
    refresh();
    return reviewQueueStore.subscribe(refresh);
  }, [refresh]);

  return { app, refresh };
}

export async function decideServerReview(
  userId: string,
  decision: "approved" | "rejected" | "needs_resubmit",
  note?: string
) {
  return moderateUser(userId, decision, note);
}
